/**
 * StatusFilterPills — reusable cumulative status filter for list pages
 * (#277 / #277-deferred, 2026-06-08).
 *
 * #39 (2026-06-11, Pedro's decision): FOUR-BUCKET UNION semantics.
 * Active / Inactive / Closed / Deleted are parallel buckets — the list
 * shows exactly the union of the selected pills. Deleted rows belong to
 * the Deleted bucket regardless of their stored lifecycle status.
 * Nothing selected = the caller renders an empty list with a hint and
 * SKIPS the fetch (protecting legacy no-param callers like pickers,
 * for whom "no params" still means all-alive).
 *
 * Caller owns the state and the consequent backend params:
 *   - statuses: LifecycleStatus[]  →  ?status=Active&status=...
 *   - includeDeleted: boolean      →  ?include_deleted=true
 *     (status + include_deleted = union; include_deleted alone = only
 *      the deleted bucket — see the routers' #39 blocks)
 *
 * Soft-delete-only entities (Persons, Policy Parties) pass
 * `availableStatuses={['Active']}` for the two-bucket variant: the
 * Active chip means "alive", Deleted means the deleted bucket, and the
 * caller maps the combination to include_deleted / only_deleted.
 *
 * Color palette matches the row badges so the filter pills and the
 * resulting badges read as the same vocabulary.
 *
 * P47 (Pedro 2026-10-16): each pill explains on hover what the status means for this
 * kind of record (`entity`, wording in utils/statusHelp.ts, shared with the row badges).
 */
import { Chip, Group, Text } from '@mantine/core';

import type { LifecycleStatus } from '../api/types';
import type { StatusHelpEntity } from '../utils/statusHelp';
import { StatusExplained } from './StatusExplained';

const STATUS_CHIP_COLOR: Record<LifecycleStatus, string> = {
  Active: 'green', Inactive: 'yellow', Closed: 'gray', Prospect: 'grape',
};

interface Props {
  statuses: LifecycleStatus[];
  onStatusesChange: (next: LifecycleStatus[]) => void;
  includeDeleted: boolean;
  onIncludeDeletedChange: (next: boolean) => void;
  /** #39: subset of lifecycle chips to render. Default all three.
   *  Soft-delete-only entities pass ['Active'] (alive bucket). */
  availableStatuses?: LifecycleStatus[];
  /** Display labels per chip (the value stays the status). P46: persons show
   *  "No current role" for the derived Inactive bucket. */
  labels?: Partial<Record<LifecycleStatus, string>>;
  /** P47: which kind of record the list holds, for the hover explanations. */
  entity: StatusHelpEntity;
}

export function StatusFilterPills({
  statuses,
  onStatusesChange,
  includeDeleted,
  onIncludeDeletedChange,
  availableStatuses = ['Active', 'Inactive', 'Closed'],
  labels,
  entity,
}: Props) {
  return (
    <Group gap="xs" align="center">
      <Text size="sm" c="dimmed">Status:</Text>
      <Chip.Group
        multiple
        value={statuses}
        onChange={(v) => onStatusesChange(v as LifecycleStatus[])}
      >
        <Group gap="xs">
          {availableStatuses.map((s) => (
            <StatusExplained key={s} entity={entity} status={s}>
              <Chip value={s} color={STATUS_CHIP_COLOR[s]} variant="light">
                {labels?.[s] ?? s}
              </Chip>
            </StatusExplained>
          ))}
        </Group>
      </Chip.Group>
      <StatusExplained entity={entity} status="Deleted">
        <Chip
          checked={includeDeleted}
          onChange={onIncludeDeletedChange}
          color="red"
          variant="light"
        >
          Deleted
        </Chip>
      </StatusExplained>
    </Group>
  );
}

/** #39: shared empty-state hint for zero selected pills. */
export const NO_STATUS_SELECTED_HINT =
  'Select at least one status above to see records.';
